import { setSnackbarPanelAction } from '../store/general/actions';
import { AppDispatch } from '../store/types';
import { AuthManager } from '../../common/utils/auth-manager';

// The backend bounces the browser back here after a shared-auth-service login with ?authError=<code>
// (routes/authErrorCodes.ts) or ?auth=1 on success. Runs before <App/> mounts, so the params are
// consumed before the router or any page logic can navigate away and drop the query string.

// Codes map to text here, never the other way round: the query string is attacker-controllable, so
// rendering whatever it carries would let anyone put arbitrary content in the app's own UI. Keep in
// sync with the backend's EAuthError and the copy in backend/html/landing.html.
const AUTH_ERROR_FALLBACK = 'Authentication failed.';
const AUTH_ERRORS = new Map<string, string>([
  ['not_configured', 'Sign-in is not configured.'],
  ['no_code', 'Sign-in expired — please try again.'],
  ['no_email', 'No email in account.'],
  ['failed', AUTH_ERROR_FALLBACK]
]);

// GA4's recommended sign_up / login events (the CEO dashboard reads them per app); gtag only exists
// on the production host (index.html), and the event is queued before any navigation away.
const reportAuthEvent = (name: 'sign_up' | 'login') => {
  // @ts-ignore
  if (typeof window.gtag === 'function') {
    // @ts-ignore
    window.gtag('event', name, { method: 'google' });
  }
};

export const consumeAuthCallbackParams = (dispatch: AppDispatch) => {
  const params = new URLSearchParams(window.location.search);
  const authError = params.get('authError');
  if (!authError && !params.has('auth')) return;

  const signup = params.has('signup');
  params.delete('authError');
  params.delete('auth');
  params.delete('signup');
  const search = params.toString();
  const cleanUrl = window.location.pathname + (search ? '?' + search : '') + window.location.hash;

  // A successful login lands on `/?auth=1` whatever page the browser started from, so send it back
  // where it came from (AuthManager.rememberReturnUrl stashed it before redirecting). Compared
  // against the *cleaned* URL, not the one still carrying `auth`: in the common case the user
  // started on `/` and the stash matches where we already are, and a navigation there would reload
  // the bundle and re-run the whole auth bootstrap for nothing. Only a genuinely different path
  // (`/call/<roomId>`) is worth a real navigation, which the router needs anyway to re-resolve the
  // route; `replace` keeps the intermediate `/?auth=1` out of the history, and since the target
  // carries no `auth` this can't bounce twice.
  if (!authError) {
    reportAuthEvent(signup ? 'sign_up' : 'login');
    const returnUrl = AuthManager.consumeReturnUrl();
    if (returnUrl && returnUrl !== cleanUrl) {
      window.location.replace(returnUrl);
      return;
    }
  }

  window.history.replaceState(null, '', cleanUrl);

  if (authError) {
    dispatch(setSnackbarPanelAction(true, AUTH_ERRORS.get(authError) || AUTH_ERROR_FALLBACK));
  }
};
